/* eslint-disable react/no-unknown-property */
import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import get from 'lodash/get';
import { useDispatch } from 'react-redux';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { createFilter } from '../../actions/filters';
import StackGrid from '../StackGrid';
import Dropdown from '../Dropdown';
import TextButton from '../TextButton';
import RadioButtonGroup from '../RadioButtonGroup';
import { channels, currencies, geographies, inclusions } from '../../constants';
import { filterSentence, mapKeyToText } from '../../utils';
import styles from './FilterForm.style';

const FilterForm = React.forwardRef(({ className: classNameProp, products, onSuccess }, ref) => {
  const dispatch = useDispatch();
  const className = clsx(classNameProp);

  const initialValues = {
    type: '',
    geographies: [],
    channels: [],
    currencies: [],
    products: [],
  };

  const FilterFormSchema = Yup.object().shape({
    type: Yup.string().required('Required'),
  });

  const showCustomDescription = values => {
    const {
      type,
      geographies: geography,
      channels: channel,
      currencies: currency,
      products: product,
    } = values;

    if (type && (geography.length || channel.length || currency.length || product.length)) {
      return true;
    }

    return false;
  };

  return (
    <Formik
      innerRef={ref}
      className={className}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={FilterFormSchema}
      onSubmit={async (values, { setErrors }) => {
        const response = await dispatch(createFilter(values));
        if (response.error && response.payload.message) {
          setErrors({ submit: response.payload.message });
          return response;
        }
        const filter = get(
          response,
          `payload.entities.filters.${get(response, 'payload.result')}`,
          {},
        );

        await onSuccess(filter);
        return response;
      }}
    >
      {({ isSubmitting, isValid, dirty, errors, submitForm, values }) => (
        <div className={styles.root}>
          <StackGrid smCols={1} mdCols={2} lgCols={4} gap="16" shrink>
            {errors.submit && (
              <MessageBar
                messageBarType={MessageBarType.error}
                messageBarIconProps={{ iconName: 'Info' }}
                isMultiline
                dismissButtonAriaLabel="Close"
                nowrap
              >
                {errors.submit}
              </MessageBar>
            )}
            <Field
              name="type"
              options={inclusions}
              component={RadioButtonGroup}
              className={styles.choiceGroup}
              required
              nowrap
            />
            <Field
              name="geographies"
              label="Geography"
              placeholder="Select"
              options={geographies}
              component={Dropdown}
              multiSelect
            />
            <Field
              name="channels"
              label="Channel"
              placeholder="Select"
              options={channels}
              component={Dropdown}
              multiSelect
            />
            <Field
              name="currencies"
              label="Currency"
              placeholder="Select"
              options={currencies}
              component={Dropdown}
              multiSelect
            />
            <Field
              disabled={!products.length}
              name="products"
              label="Product"
              placeholder="Select"
              options={products}
              component={Dropdown}
              multiSelect
            />
            <div className={styles.save} nowrap="true">
              <div className={styles.filterSentenceContainer}>
                {showCustomDescription(values) && (
                  <div
                    className={styles.filterSentence}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={filterSentence({
                      type: values.type,
                      currencies: values.currencies,
                      channels: mapKeyToText(values.channels, channels),
                      products: mapKeyToText(values.products, products),
                      geographies: mapKeyToText(values.geographies, geographies),
                    })}
                  />
                )}
              </div>
              {isSubmitting && (
                <Spinner size={SpinnerSize.small} className={styles.loadingSpinner} />
              )}
              <TextButton
                disabled={
                  !(isValid && dirty) ||
                  isSubmitting ||
                  !(
                    isValid &&
                    (values.geographies.length ||
                      values.currencies.length ||
                      values.channels.length ||
                      values.products.length)
                  )
                }
                type="button"
                onClick={submitForm}
                noPadding
                className={styles.saveFilterButton}
              >
                Save Filter
              </TextButton>
            </div>
          </StackGrid>
        </div>
      )}
    </Formik>
  );
});

FilterForm.propTypes = {
  className: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onSuccess: PropTypes.func,
};

FilterForm.defaultProps = {
  className: '',
  onSuccess: () => {},
};

export default FilterForm;
